import { ChangeEventHandler, useEffect, useRef } from 'react';

import { Controller } from 'react-hook-form';
import { defaultTheme as reactSelectDefaultTheme } from 'react-select';

import { patchedStoryblokEditable } from '../../../utils/patchedStoryblokEditable';
import { useFormBuilderContext } from '../hooks/useFormBuilderContext';
import { createRHFControllerValidationRules } from '../utils/validation';
import {
  FormBuilderInputElement,
  FormBuilderInputElementErrorMessage,
  FormBuilderInputElementWrapper,
} from './FormBuilderInput.styles';
import { FormBuilderInputProps } from './FormBuilderInput.types';
import { UKPhoneInput, validateUKPhoneNumber } from './UKPhoneInput';

export const FormBuilderInput = (props: FormBuilderInputProps) => {
  const { blok } = props;
  const { key, placeholder, type, includeInSubmitRequest, validationRules } = blok;

  const { keyValues, setKeyValue, includeKeyInSubmitRequest, hookForm } = useFormBuilderContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const value = keyValues[key] ?? '';
  const isPhone = type === 'tel';

  const hookFormErrorMessage = hookForm.formState.errors[key]?.message || null;
  const errorMessage = typeof hookFormErrorMessage === 'string' ? hookFormErrorMessage : null;

  const controllerRules = createRHFControllerValidationRules(validationRules);

  useEffect(() => {
    const existingValue = keyValues[key];

    if (existingValue) {
      hookForm.setValue(key, existingValue);
    } else {
      setKeyValue(key, '');
    }

    if (includeInSubmitRequest) {
      includeKeyInSubmitRequest(key);
    }
  }, []);

  const handleInput: ChangeEventHandler<HTMLInputElement> = (event) => {
    setKeyValue(key, event.target.value);
  };

  if (isPhone) {
    return (
      <FormBuilderInputElementWrapper {...patchedStoryblokEditable(blok)}>
        <Controller
          name={key}
          control={hookForm.control}
          rules={{
            ...controllerRules,
            validate: (val) => {
              const baseValidation = controllerRules?.validate;
              if (typeof baseValidation === 'function') {
                const result = baseValidation(val, {});
                if (result !== true) return result;
              }
              return validateUKPhoneNumber(val);
            },
          }}
          render={({ field }) => (
            <UKPhoneInput
              ref={(instance) => {
                field.ref(instance);
                inputRef.current = instance;
              }}
              name={key}
              placeholder={placeholder}
              value={value}
              onChange={(fullValue) => {
                setKeyValue(key, fullValue);
                field.onChange(fullValue);
              }}
              onBlur={field.onBlur}
              style={{
                borderColor: reactSelectDefaultTheme.colors.neutral20,
              }}
            />
          )}
        />
        {!!errorMessage && (
          <FormBuilderInputElementErrorMessage>{errorMessage}</FormBuilderInputElementErrorMessage>
        )}
      </FormBuilderInputElementWrapper>
    );
  }

  return (
    <FormBuilderInputElementWrapper {...patchedStoryblokEditable(blok)}>
      <Controller
        name={key}
        control={hookForm.control}
        rules={{
          ...controllerRules,
          validate: () => {
            const input = inputRef.current;
            if (input && !input.checkValidity()) {
              return input.validationMessage;
            }
            return true;
          },
        }}
        render={({ field }) => (
          <FormBuilderInputElement
            {...field}
            ref={(instance) => {
              field.ref(instance);
              inputRef.current = instance;
            }}
            name={key}
            autoComplete="on"
            placeholder={placeholder}
            type={type}
            onInput={handleInput}
            value={value}
            style={{
              borderColor: reactSelectDefaultTheme.colors.neutral20,
            }}
          />
        )}
      />
      {!!errorMessage && (
        <FormBuilderInputElementErrorMessage>{errorMessage}</FormBuilderInputElementErrorMessage>
      )}
    </FormBuilderInputElementWrapper>
  );
};
